import React, { useMemo } from "react";

/**
 * AnimatedBackground — combination of:
 *  - Slow-zooming dark ice rink image
 *  - Drifting snow/frost particles
 *  - Streaking hockey pucks with red light trails
 *  - Pulsing red ice-crack vector lines
 *  - Grain + vignette overlays
 */
const AnimatedBackground = ({ image, intensity = "high", showCracks = true }) => {
  const snowCount = intensity === "high" ? 60 : intensity === "medium" ? 35 : 18;
  const puckCount = intensity === "high" ? 6 : intensity === "medium" ? 4 : 2;

  const snowflakes = useMemo(
    () =>
      Array.from({ length: snowCount }).map((_, i) => ({
        left: Math.random() * 100,
        size: 1 + Math.random() * 4,
        duration: 8 + Math.random() * 14,
        delay: -Math.random() * 22,
        opacity: 0.2 + Math.random() * 0.6,
      })),
    [snowCount]
  );

  const pucks = useMemo(
    () =>
      Array.from({ length: puckCount }).map((_, i) => ({
        py: 10 + Math.random() * 80,
        duration: 2.4 + Math.random() * 3,
        delay: -Math.random() * 6,
        width: 140 + Math.random() * 160,
      })),
    [puckCount]
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" data-testid="animated-bg">
      {/* Hero image with slow zoom */}
      {image && (
        <div className="absolute inset-0 slow-zoom">
          <img
            src={image}
            alt=""
            className="w-full h-full object-cover opacity-40"
            draggable="false"
          />
        </div>
      )}

      {/* Diagonal red stripes texture */}
      <div className="absolute inset-0 diagonal-stripes opacity-60" />

      {/* Ice cracks SVG */}
      {showCracks && (
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 1920 1080"
          preserveAspectRatio="none"
          style={{ mixBlendMode: "screen" }}
        >
          <g stroke="#E60012" strokeWidth="1.2" fill="none" opacity="0.7">
            <polyline className="crack-line" style={{ animationDelay: "0s" }}
              points="0,200 240,180 360,260 540,220 700,300 920,250 1080,320 1280,260 1500,330 1700,280 1920,340" />
            <polyline className="crack-line" style={{ animationDelay: "1.2s" }}
              points="0,820 180,780 320,860 480,800 660,880 880,820 1060,900 1280,840 1480,920 1700,860 1920,900" />
            <polyline className="crack-line" style={{ animationDelay: "2.4s" }}
              points="200,0 280,140 240,260 320,420 280,560 380,700 340,860 420,1000" />
            <polyline className="crack-line" style={{ animationDelay: "0.6s" }}
              points="1620,0 1560,160 1640,300 1580,460 1660,600 1580,760 1660,900 1600,1080" />
          </g>
          <g stroke="#ffffff" strokeWidth="0.6" fill="none" opacity="0.25">
            <polyline points="0,500 240,470 480,520 720,480 960,540 1200,490 1440,560 1680,500 1920,560" />
          </g>
        </svg>
      )}

      {/* Puck streaks */}
      {pucks.map((p, i) => (
        <span
          key={`puck-${i}`}
          className="puck-streak"
          style={{
            "--py": `${p.py}vh`,
            top: 0,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            width: `${p.width}px`,
          }}
        />
      ))}

      {/* Snowflakes */}
      {snowflakes.map((s, i) => (
        <span
          key={`snow-${i}`}
          className="snowflake"
          style={{
            left: `${s.left}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            opacity: s.opacity,
            animationDuration: `${s.duration}s`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}

      {/* Grain */}
      <div className="noise-overlay" />

      {/* Vignette + dark overlay for legibility */}
      <div className="absolute inset-0 vignette" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80" />
    </div>
  );
};

export default AnimatedBackground;
